import { useState, useEffect } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Grid
} from "@mui/material";
import { Assessment, Save, PictureAsPdf, GetApp } from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logoImg from "../../../Images/logo_pmpc.png";
import "./ActiveProjectCodesReport.css";

const ProjectCodesReport = () => {
  const [department, setDepartment] = useState("ALL");
  const [departmentOptions, setDepartmentOptions] = useState([{ value: "ALL", label: "All Departments" }]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [reportGenerated, setReportGenerated] = useState(false);

  useEffect(() => {
    const loadDepartments = async () => {
      setLoadingDepartments(true);

      try {
        const response = await axiosInstance.get("/departments/");
        const options = [
          { value: "ALL", label: "All Departments" },
          ...response.data.map((dept) => ({
            value: dept.name,
            label: dept.name,
          })),
        ];

        setDepartmentOptions(options);
      } catch (err) {
        setError("Failed to load departments");
      } finally {
        setLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  const filterReportByDepartment = (data) => {
    if (department === "ALL") {
      return data;
    }

    const filteredDepartments = data.departments.filter(
      (item) => item.department === department
    );

    const totalProjects = filteredDepartments.reduce(
      (total, item) => total + item.count,
      0
    );

    return {
      ...data,
      departments: filteredDepartments,
      total_projects: totalProjects,
      selected_department: department,
    };
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError("");
    setReportData(null);
    setReportGenerated(false);

    try {
      const response = await axiosInstance.get("/active-projectcode-report/");
      const filteredData = filterReportByDepartment(response.data);

      setReportData(filteredData);
      setReportGenerated(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        "Failed to generate active project codes report"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentChange = (value) => {
    setDepartment(value);
    setReportGenerated(false);
    setReportData(null);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportToExcel = async () => {
    if (!reportData || !reportData.departments || reportData.departments.length === 0) {
      alert("No data to export!");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Active Project Codes");

    try {
      sheet.mergeCells("A1:A4");
      const logo = workbook.addImage({
        buffer: await fetch(logoImg).then((res) => res.arrayBuffer()),
        extension: "png",
      });

      sheet.addImage(logo, {
        tl: { col: 0, row: 0 },
        br: { col: 1, row: 4 },
      });
    } catch (error) {
      console.warn("Logo could not be loaded:", error);
    }

    sheet.mergeCells("B1:C4");
    const titleCell = sheet.getCell("B1");
    titleCell.value = "Active Project Codes Report";
    titleCell.font = { size: 14, bold: true };
    titleCell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };

    sheet.insertRow(5, []);
    sheet.insertRow(6, [
      `Department: ${department === "ALL" ? "All Departments" : department}`,
    ]);
    sheet.getCell("A6").font = { size: 10 };
    sheet.getCell("A6").alignment = { horizontal: "left" };

    sheet.insertRow(7, []);

    let currentRow = 8;

    reportData.departments.forEach((dept) => {
      sheet.mergeCells(`A${currentRow}:B${currentRow}`);
      const departmentCell = sheet.getCell(`A${currentRow}`);
      departmentCell.value = {
        richText: [
          {
            text: `${dept.department} `,
            font: {
              bold: true,
              size: 11,
            },
          },
          {
            text: `(${dept.count})`,
            font: {
              bold: false,
              size: 8,
            },
          },
        ],
      };
      departmentCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE8E8E8" },
      };
      departmentCell.alignment = { vertical: "middle", horizontal: "left" };

      currentRow += 1;

      const headerRow = sheet.getRow(currentRow);
      headerRow.values = ["Project Code", "Description"];
      headerRow.font = { bold: true };

      headerRow.eachCell((cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF5F5F5" },
        };
      });

      currentRow += 1;

      dept.projects.forEach((project) => {
        const row = sheet.getRow(currentRow);
        row.values = [project.project_code, project.description || "-"];
        row.getCell(1).alignment = { vertical: "top", horizontal: "left" };
        row.getCell(2).alignment = {
          vertical: "top",
          horizontal: "left",
          wrapText: true,
        };

        currentRow += 1;
      });

      currentRow += 2;
    });

    sheet.columns = [
      { width: 35 },
      { width: 80 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "ActiveProjectCodesReport.xlsx");
  };

  const exportPDF = () => {
    if (!reportData || !reportData.departments || reportData.departments.length === 0) {
      alert("No data to export!");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const logo = "/logo_pmpc.png";

    pdf.addImage(logo, "PNG", 8, 3, 28, 8);

    const triangleWidth = 20;
    const totalWidth = pdfWidth * 0.25;
    const barWidth = totalWidth - triangleWidth;

    const barHeight = 1.2;
    const secondBarHeight = 2;
    const barTop = 15;
    const barBottom = barTop + barHeight;

    pdf.setFillColor(23, 52, 88);
    pdf.rect(0, barTop, barWidth + 1, barHeight, "F");

    pdf.triangle(
      barWidth,
      barBottom,
      barWidth + triangleWidth,
      barBottom,
      barWidth,
      barTop,
      "F"
    );

    const headerLongBarTop = barBottom - 0.6;
    pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

    const dottedLineY = headerLongBarTop + secondBarHeight;

    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Active Project Codes Report", pdfWidth / 2, dottedLineY + 12, {
      align: "center",
    });

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      `Department: ${department === "ALL" ? "All Departments" : department}`,
      10,
      dottedLineY + 20
    );

    let currentY = dottedLineY + 28;

    reportData.departments.forEach((dept) => {
      if (currentY > pdfHeight - 50) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);

      const departmentText = `${dept.department} `;
      pdf.text(departmentText, 10, currentY);

      const deptWidth = pdf.getTextWidth(departmentText);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);

      pdf.text(`(${dept.count})`, 10 + deptWidth, currentY);
      currentY += 4;

      const tableData = dept.projects.map((project) => [
        project.project_code,
        project.description || "-",
      ]);

      autoTable(pdf, {
        startY: currentY,
        head: [["Project Code", "Description"]],
        body: tableData,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          lineColor: [180, 180, 180],
          lineWidth: 0.25,
          valign: "middle",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [232, 232, 232],
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 125 },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: { left: 10, right: 10 },
        tableWidth: "auto",
        theme: "grid",
      });

      currentY = pdf.lastAutoTable.finalY + 12;
    });

    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFillColor(23, 52, 88);

      const bottomOffset = 12;
      const longBarTop = pdfHeight - bottomOffset;

      pdf.rect(0, longBarTop - 0.6 + 0.1, pdfWidth, secondBarHeight, "F");

      const shortBarTop = longBarTop + secondBarHeight - 0.6;
      const shortBarBottom = shortBarTop + barHeight;

      pdf.rect(0, shortBarTop, barWidth + 1, barHeight, "F");

      pdf.triangle(
        barWidth,
        shortBarTop,
        barWidth + triangleWidth,
        shortBarTop,
        barWidth,
        shortBarBottom,
        "F"
      );

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);

      const today = new Date();
      const currentDate = today.toLocaleDateString("ro-RO");
      pdf.text(`${currentDate}`, pdfWidth - 30, pdfHeight - 3);

      pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, {
        align: "center",
      });
    }

    pdf.save("ActiveProjectCodesReport.pdf");
  };

  return (
    <Container maxWidth={false} disableGutters className="proj-report project-report">
      <Container maxWidth={false} disableGutters className="project-container">
        <Typography variant="h4" gutterBottom marginTop="20px">
          Active Project Codes Report
        </Typography>

        <Paper className="filters-paper" sx={{ p: 2 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleGenerateReport();
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={9}>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                  <TextField
                    select
                    label="Department"
                    value={department}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    required
                    size="small"
                    className="department-select"
                    disabled={loadingDepartments}
                    sx={{
                      minWidth: 220,
                      '& .MuiFormLabel-asterisk': {
                        color: 'red'
                      }
                    }}
                  >
                    {departmentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                    className="generate-button"
                    startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                    disabled={loading || loadingDepartments}
                  >
                    {loading ? "Generating..." : "Generate Report"}
                  </Button>

                  {reportData && (
                    <Chip
                      label={`Total active project codes: ${reportData.total_projects}`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Grid>

              {reportGenerated && reportData?.departments?.length > 0 && (
                <Grid item xs={12} md={3}>
                  <Grid
                    container
                    spacing={1}
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleClick}
                        className="save-button"
                        startIcon={<Save />}
                        sx={{
                          backgroundColor: "#4caf50",
                          "&:hover": {
                            backgroundColor: "#388e3c",
                          },
                        }}
                      >
                        Save As
                      </Button>
                    </Grid>
                  </Grid>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        exportPDF();
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(211, 47, 47, 0.08)",
                          "& .MuiListItemIcon-root": {
                            color: "#b71c1c",
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: "#d32f2f" }}>
                        <PictureAsPdf fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Save as PDF" />
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        handleClose();
                        exportToExcel();
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(27, 94, 32, 0.08)",
                          "& .MuiListItemIcon-root": {
                            color: "#0d3c12",
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: "#1b5e20" }}>
                        <GetApp fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Save as Excel" />
                    </MenuItem>
                  </Menu>
                </Grid>
              )}
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        </Paper>

        {reportData && reportGenerated && (
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            {reportData.departments?.length > 0 ? (
              reportData.departments.map((department) => (
                <Paper key={department.department} sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">
                      {department.department}
                    </Typography>

                    <Chip
                      label={`${department.count}`}
                      size="small"
                      className="department-count-chip"
                    />
                  </Box>

                  <TableContainer className="report-table-container">
                    <Table size="small" sx={{ tableLayout: "fixed" }}>
                      <TableHead>
                        <TableRow className="table-header">
                          <TableCell
                            className="table-header-cell"
                            sx={{ width: "35%" }}
                          >
                            Project Code
                          </TableCell>

                          <TableCell
                            className="table-header-cell"
                            sx={{ width: "65%" }}
                          >
                            Description
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {department.projects.map((project) => (
                          <TableRow key={project.project_code} hover>
                            <TableCell>{project.project_code}</TableCell>
                            <TableCell>{project.description || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))
            ) : (
              <Alert severity="info">
                No active project codes found for the selected department.
              </Alert>
            )}
          </Box>
        )}

        {reportData && reportGenerated && (
          <Box className="report-footer">
            <Typography variant="caption" className="footer-text">
              Generated on {reportData.report_date} | Status: Active |
              Department:{" "}
              {department === "ALL"
                ? "All Departments"
                : department}
            </Typography>
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default ProjectCodesReport;